import React, { useState } from 'react';
import { useSelector, useDispatch } from "react-redux";
import {
	Alert, Box, Button, Stepper, Step, StepLabel, Typography,
	Radio, RadioGroup, FormControlLabel, Dialog, DialogActions,
	DialogContent, DialogContentText, DialogTitle,
} from '@mui/material';
import MonthlyCostStatusEmailDrafter from './MonthlyCostStatusEmailDrafter';
import RevenueSpreadCard from './RevenueSpreadCard';
import ProcoreStageDatesStep from './ProcoreStageDatesStep';
import { setShowSettingsDialogBox, setSettingsDialogBoxTab,setMonthlyCostStatusData, setMonthlyCostStatusEmailFormData, setSelectedProjectId, setProjectData, mergeMonthlyCostStatusData } from '../../../../store/slices/projectSlice';
import { confirmProcoreDates, fetchMonthlyCostStatusSubmissionData, fetchProjectData, saveMonthlyCostStatusSubmissionData } from '../../Containers/api';
import { saveLandedOnServer } from './costStatusDraftSync';
import { clearDraft as clearLocalDraft } from '../../Containers/phdDraftStorage';
import { toast } from "react-hot-toast";
import { toastStyle } from "../../Containers/continerUtilityFunctions";
import CircularProgress from '@mui/material/CircularProgress';

const steps = ['Select Snapshot', 'Confirm Procore Stage & Dates', 'Confirm Revenue Spread', 'Draft Cost Status Email'];
const PROCORE_STEP = 1;
const REVENUE_STEP = 2;
const EMAIL_STEP = 3;

// Define Redux Selectors
const useReduxSelectors = () => {
	const revenueSpreadData = useSelector(
		(state) => state.project.revenueSpreadData
	);
	const projectId = useSelector(
		(state) => state.project.selectedProjectId
	);
	const monthlyCostStatusEmailFormData = useSelector(
		(state) => state.project.monthlyCostStatusEmailFormData
	);
	const monthlyCostStatusEmailFormFiles = useSelector(
		(state) => state.project.monthlyCostStatusEmailFormFiles
	);

	const monthlyCostStatusData = useSelector(
		(state) => state.project.monthlyCostStatusData
	);

	const settingsData = useSelector(
		(state) => state.project.settingsData
	);

	const projectData = useSelector(
		(state) => state.project.projectData
	);

	return {
		revenueSpreadData,
		projectId,
		monthlyCostStatusEmailFormData,
		monthlyCostStatusEmailFormFiles,
		monthlyCostStatusData,
		settingsData,
		projectData
	};
};

export default function CostStatusWorkflowStepper({ handleCloseModal, snapshots = [] }) {
	const dispatch = useDispatch();
	const {
		revenueSpreadData,
		projectId,
		monthlyCostStatusEmailFormData,
		monthlyCostStatusEmailFormFiles,
		monthlyCostStatusData,
		settingsData,
		projectData
	} = useReduxSelectors();
	const [activeStep, setActiveStep] = useState(0);

	const [selectedSnapshot, setSelectedSnapshot] = useState(null);
	const [draftReviewers, setDraftReviewers] = useState([]);
	const procoreConfirmation = monthlyCostStatusData?.procore_dates_confirmation || null;
	const [procoreConfirmed, setProcoreConfirmed] = useState(!!procoreConfirmation);
	const [procoreConfirming, setProcoreConfirming] = useState(false);
	const [procoreRefreshing, setProcoreRefreshing] = useState(false);
	const [procoreError, setProcoreError] = useState(null);
	const [revenueConfirmed, setRevenueConfirmed] = useState(false);
	const [submitting, setSubmitting] = useState(false);
	const [submissionError, setSubmissionError] = useState(null);
	const [confirmationOpen, setConfirmationOpen] = useState(false);
	const [pendingSubmissionMode, setPendingSubmissionMode] = useState(null);
	React.useEffect(() => {
		setSelectedSnapshot(monthlyCostStatusData?.selected_snapshot || null);
	// Keyed on the snapshot itself: an autosave replaces monthlyCostStatusData without
	// touching it, and must not reset the PM's pick to the (possibly empty) saved one.
	}, [monthlyCostStatusData?.selected_snapshot]);

	React.useEffect(() => {
		// Only adopt reviewers when the settings payload actually carries them. A
		// payload without the key (e.g. the optimistic write after saving project
		// settings) must not blank the selection, since an empty list posted back
		// clears the saved reviewers.
		const savedReviewers = settingsData?.monthly_cost_status_data?.cost_status_draft_reviewers;
		if (savedReviewers) setDraftReviewers(savedReviewers);
	}, [settingsData]);

	React.useEffect(() => {
		if (procoreConfirmation) setProcoreConfirmed(true);
	}, [procoreConfirmation]);

	const handleConfirmProcoreDates = async () => {
		setProcoreConfirming(true);
		setProcoreError(null);
		try {
			const response = await confirmProcoreDates(projectId);
			dispatch(mergeMonthlyCostStatusData({ procore_dates_confirmation: response.procore_dates_confirmation }));
			setProcoreConfirmed(true);
		} catch (error) {
			setProcoreError(`${error.message || 'Something went wrong.'} Your confirmation was not saved. Please try again.`);
		} finally {
			setProcoreConfirming(false);
		}
	};

	const handleRefreshProcoreDates = async () => {
		setProcoreRefreshing(true);
		setProcoreError(null);
		try {
			const refreshed = await fetchProjectData(projectId);
			if (refreshed) dispatch(setProjectData(refreshed));
		} catch (error) {
			setProcoreError(`${error.message || 'Something went wrong.'} Could not reload the Procore values.`);
		} finally {
			setProcoreRefreshing(false);
		}
	};

	const handleConfirmSubmission = () => {
		setConfirmationOpen(false);
		if (pendingSubmissionMode) {
			performSubmission(pendingSubmissionMode);
		}
		setPendingSubmissionMode(null);
	};

	const handleCancelSubmission = () => {
		setConfirmationOpen(false);
		setPendingSubmissionMode(null);
	};

	const performSubmission = async (mode) => {
		setSubmitting(true);
		setSubmissionError(null);

		// Sanitize the email draft if needed.
		// Here mode = submitted if not specified otherwise.
		// NEW FILES
		let new_files = monthlyCostStatusEmailFormFiles.filter(file => file instanceof File);
		let existing_files = monthlyCostStatusEmailFormFiles.filter(file => !(file instanceof File)).map(file => file.id);
		
		const sentStatus = mode && mode.includes("save") ? "Draft" : "Submitted";
		try {
			let body = {
				'project_id': projectId,
				'mode': mode,
				'cost_status': sentStatus,
				'cost_status_data': monthlyCostStatusEmailFormData,
				'snapshot_id': selectedSnapshot,
				'files': new_files,
				'existing_files': existing_files,
				'draft_reviewers': draftReviewers.map((reviewer) => reviewer.email),
			}
			const updatedMonthlyCostStatusData = await saveMonthlyCostStatusSubmissionData(body);
			if (updatedMonthlyCostStatusData?.error) {
				throw new Error(updatedMonthlyCostStatusData.error);
			}
			if (updatedMonthlyCostStatusData?.warning) {
				keepEditingAfterWarning(updatedMonthlyCostStatusData);
				return;
			}
			finishSuccessfulSave(mode, updatedMonthlyCostStatusData);
		} catch (error) {
			const server = await fetchServerCopyQuietly();
			if (saveLandedOnServer({ loaded: monthlyCostStatusData, sent: monthlyCostStatusEmailFormData, sentStatus, server })) {
				finishSuccessfulSave(mode, server);
				return;
			}
			const reason = error.message || 'An error occurred while saving the form.';
			setSubmissionError(`${reason} Your changes were not saved, but your text is still here. Please try again.`);
			setSubmitting(false);
		}
	};

	const fetchServerCopyQuietly = async () => {
		try {
			return await fetchMonthlyCostStatusSubmissionData(projectId);
		} catch {
			return null;
		}
	};

	// The server kept the text but skipped the draft email (e.g. no PDF
	// attached), so stay in the form and say what is missing.
	const keepEditingAfterWarning = (serverCopy) => {
		const { warning, ...copy } = serverCopy;
		dispatch(setMonthlyCostStatusData(copy));
		clearLocalDraft({ projectId, month: copy.month || monthlyCostStatusData?.month || null });
		setSubmissionError(`Your text was saved, but the draft email was not sent. ${warning}`);
		setSubmitting(false);
	};

	const finishSuccessfulSave = (mode, serverCopy) => {
		if (serverCopy) {
			dispatch(setMonthlyCostStatusData(serverCopy));
		}
		// The server copy is now authoritative; a leftover local draft would
		// re-restore over it on the next open.
		clearLocalDraft({ projectId, month: serverCopy?.month || monthlyCostStatusData?.month || null });

		toast.success("This page will refresh in 5 seconds.", toastStyle());
		if (mode === "save") {
			toast.success("Monthly Cost Status Data saved successfully.", toastStyle());
		} else if (mode === "save_and_email") {
			const reviewerCount = draftReviewers.length;
			toast.success(
				reviewerCount > 0
					? `Monthly Cost Status Data saved and Email draft sent to you and ${reviewerCount} reviewer(s).`
					: "Monthly Cost Status Data saved and Email draft sent successfully.",
				toastStyle()
			);
		} else {
			toast.success("Monthly Cost Status Email was sent successfully.", toastStyle());
		}

		setTimeout(() => {
			// Close modal first to prevent state reset issues
			handleCloseModal();
			setSubmitting(false);

			// Then refresh project data after modal is closed
			setTimeout(() => {
				let current_project_id = projectId;
				dispatch(setSelectedProjectId(null));
				dispatch(setSelectedProjectId(current_project_id));
			}, 100);
		}, 5000);
	};

	const handleNext = (mode) => {
		if (activeStep === steps.length - 1) {
			// For actual submission (not save or save_and_email), show confirmation dialog
			if (mode === 'submit') {
				setPendingSubmissionMode(mode);
				setConfirmationOpen(true);
			} else {
				// For save and save_and_email, proceed directly without confirmation
				performSubmission(mode);
			}
		} else {
			setActiveStep((prev) => prev + 1);
		}
	};

	const handleBack = () => {
		if (activeStep === 0) {
			handleCloseModal();
			return;
		}
		setActiveStep((prev) => prev - 1);
	};

	const isStepValid = () => {
		if (activeStep === 0) return !!selectedSnapshot;
		if (activeStep === PROCORE_STEP) return procoreConfirmed;
		if (activeStep === REVENUE_STEP) return revenueConfirmed;
		return true;
	};

	return (
		<Box sx={{ width: '100%'}}>
			{activeStep === EMAIL_STEP && (
				<Box
					sx={{
						backgroundColor: '#e8f5e9',
						color: '#388e3c',
						textAlign: 'center',
						py: 3,
						mb: 2,
						borderRadius: 1,
						fontWeight: 500,
						fontSize: '1rem',
						fontFamily: 'Titillium Web, sans-serif !important',
						border: '1px solid #c8e6c9',
					}}
				>
					<strong>New Feature (Beta):</strong> You can now use tables! We’re aware of some Outlook formatting quirks and are working to fix them. Thanks for your patience.
				</Box>
			)}
			{/* Four labels cannot share a phone-width row, so narrow screens name only the current step. */}
			<Stepper
				activeStep={activeStep}
				sx={{ mb: 3, '@media (max-width: 600px)': { '& .MuiStepLabel-label:not(.Mui-active)': { display: 'none' } } }}
			>
			{steps.map((label) => (
				<Step key={label}><StepLabel>{label}</StepLabel></Step>
			))}
			</Stepper>

			{submitting ? (
				<Box display="flex" justifyContent="center" alignItems="center" mt={2} mb={2}>
					<CircularProgress />
				</Box>
			): (
			<Box>
				{activeStep === 0 && (
					<Box>
						<Typography variant="subtitle1" gutterBottom>Select a Snapshot:</Typography>
						<RadioGroup
							value={selectedSnapshot}
							onChange={(e) => setSelectedSnapshot(e.target.value)}
						>
							{snapshots.map((snap) => (
								<FormControlLabel
									key={snap.id}
									value={snap.id}
									control={<Radio />}
									label={`${snap.name} (${snap.created_at ? new Date(snap.created_at).toLocaleDateString('en-US', { timeZone: 'America/Chicago' }) : 'N/A'})`}
								/>
							))}
						</RadioGroup>
					</Box>
				)}
				{activeStep === PROCORE_STEP && (
					<ProcoreStageDatesStep
						project={projectData?.project_data}
						confirmation={procoreConfirmation}
						confirmed={procoreConfirmed}
						confirming={procoreConfirming}
						refreshing={procoreRefreshing}
						error={procoreError}
						onConfirm={handleConfirmProcoreDates}
						onRefresh={handleRefreshProcoreDates}
						onDismissError={() => setProcoreError(null)}
					/>
				)}
				{activeStep === REVENUE_STEP && (
					<Box>
						<Typography variant="subtitle1" gutterBottom>
						Confirm Revenue Spread:
						</Typography>
						{/* Replace this with your actual table or visual confirmation UI */}
						<Box mb={2} style={{'marginBottom': '30px'}}>
							<RevenueSpreadCard revenueSpreadData={revenueSpreadData} />
						</Box>
						<div style={{ display: 'flex', justifyContent: 'flex-end', gap: 16 }}> 
							<Button
								variant={revenueConfirmed ? "outlined" : "contained"}
								onClick={() => setRevenueConfirmed(true)}
							>
								{revenueConfirmed ? "Confirmed" : "Confirm"}
							</Button>
							<Button
								variant={"contained"}
								onClick={() => {
									// Do a delay of 0.5 seconds before opening the settings dialog box
									handleCloseModal();
									setTimeout(() => {
										dispatch(setShowSettingsDialogBox(true));
										dispatch(setSettingsDialogBoxTab(1));
									}, 500);
								}}
							>
								Edit
							</Button>
						</div>
					</Box>
				)}
				{activeStep === EMAIL_STEP && (
					<Box>
						<MonthlyCostStatusEmailDrafter 
							selectedSnapshot={selectedSnapshot}
							draftReviewers={draftReviewers}
							setDraftReviewers={setDraftReviewers}
						/>
					</Box>
				)}
			</Box>

			)}
			{submissionError && (
				<Alert severity="error" sx={{ mt: 2 }} onClose={() => setSubmissionError(null)}>
					{submissionError}
				</Alert>
			)}
			{/* Navigation buttons */}
			<Box sx={{ mt: 2, display: 'flex', justifyContent: 'space-between' }}>
				<Button onClick={handleBack}>Cancel</Button>
				<div>
					{activeStep === steps.length - 1 && !(monthlyCostStatusData?.cost_status && monthlyCostStatusData?.cost_status === 'Submitted') && (
						<>
							<Button
								variant="contained"
								onClick={() => handleNext("save")}
								disabled={!isStepValid() || submitting}
							>
								Save
							</Button>&nbsp;&nbsp;&nbsp;&nbsp;
						</>
					)}
					{activeStep === steps.length - 1 && !(monthlyCostStatusData?.cost_status && monthlyCostStatusData?.cost_status === 'Submitted') && (
						<>
							<Button
								variant="contained"
								onClick={() => handleNext("save_and_email")}
								disabled={!isStepValid() || submitting}
							>
								Save + Get Draft Email
							</Button>&nbsp;&nbsp;&nbsp;&nbsp;
						</>
					)}
					<Button
						variant="contained"
						onClick={() => handleNext('submit')}
						disabled={!isStepValid() || submitting}
					>
						{activeStep === steps.length - 1 ? 'Submit' : 'Next'}
					</Button>
				</div>
			</Box>

			{/* Confirmation Dialog */}
			<Dialog
				open={confirmationOpen}
				onClose={handleCancelSubmission}
				aria-labelledby="confirmation-dialog-title"
				aria-describedby="confirmation-dialog-description"
			>
				<DialogTitle id="confirmation-dialog-title">
					Confirm Submission
				</DialogTitle>
				<DialogContent>
					<DialogContentText id="confirmation-dialog-description">
						Are you sure you want to submit the Monthly Cost Status Email? 
						This action will send the email and cannot be undone.
					</DialogContentText>
				</DialogContent>
				<DialogActions>
					<Button onClick={handleCancelSubmission} color="primary">
						No
					</Button>
					<Button 
						onClick={handleConfirmSubmission} 
						color="primary" 
						variant="contained"
						autoFocus
					>
						Yes
					</Button>
				</DialogActions>
			</Dialog>
		</Box>
	);
}